import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Loader2, MapPin, Calendar, Search, Filter, Locate, Sparkles, Tag } from 'lucide-react';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso.length === 10 ? iso + 'T00:00:00' : iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

/** /events — Hub for vending opportunities + community events.
 *  Two sections: "Near you" (when geolocation granted) and "All events".
 */
export default function EventsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: '', city: '', when: 'upcoming', open_to_vendors: '' });
  const [coords, setCoords] = useState(null);  // {lat, lng} when granted
  const [locating, setLocating] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.q) params.set('q', filters.q);
    if (filters.city) params.set('city', filters.city);
    if (filters.when) params.set('when', filters.when);
    if (filters.open_to_vendors) params.set('open_to_vendors', filters.open_to_vendors);
    if (coords) {
      params.set('lat', coords.lat);
      params.set('lng', coords.lng);
      params.set('radius_km', '100');
    }
    try {
      const res = await fetch(`${API}/events?${params.toString()}`);
      if (res.ok) setEvents((await res.json()).events || []);
    } finally { setLoading(false); }
  }, [filters, coords]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
      },
      () => setLocating(false),
      { timeout: 8000 },
    );
  };

  const nearby = events.filter((e) => coords && e.distance_km !== null && e.distance_km !== undefined && e.distance_km <= 100);
  const allList = events;

  return (
    <div className="min-h-screen bg-[#0B0D2B] text-white">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 pt-24 pb-12" data-testid="events-page">
        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div>
            <p className="inline-flex items-center gap-2 text-xs text-fuchsia-300 font-bold tracking-widest uppercase mb-2">
              <Sparkles size={14} /> Events Hub
            </p>
            <h1 className="text-3xl sm:text-4xl font-bold font-syne leading-tight">
              Vending events &<br />
              <span className="bg-gradient-to-r from-fuchsia-400 via-pink-400 to-cyan-300 bg-clip-text text-transparent">
                Indigenous opportunities.
              </span>
            </h1>
            <p className="text-zinc-400 text-sm mt-2 max-w-xl">
              Pop-up markets, festivals, conferences. Find where to sell your goods, sign up to vend, or attend as a buyer.
            </p>
          </div>
          {user && (
            <button
              onClick={() => navigate('/events/new')}
              className="flex-shrink-0 px-4 py-2.5 rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-400 hover:to-purple-500 text-white text-sm font-bold flex items-center gap-2 shadow-[0_4px_24px_-4px_rgba(168,85,247,0.7)] border border-purple-300/40"
              data-testid="events-new-cta"
            >
              <Plus size={14} /> Post an Event
            </button>
          )}
        </div>

        {/* Filter bar */}
        <div className="rounded-2xl bg-[#13153A]/80 border border-white/10 p-3 sm:p-4 flex flex-wrap items-center gap-2 mb-6" data-testid="events-filter-bar">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Search by name, tag…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:border-fuchsia-500/50"
              data-testid="events-search"
            />
          </div>
          <div className="relative flex-1 min-w-[180px]">
            <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              value={filters.city}
              onChange={(e) => setFilters({ ...filters, city: e.target.value })}
              placeholder="City"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:border-fuchsia-500/50"
              data-testid="events-city"
            />
          </div>
          <select value={filters.when} onChange={(e) => setFilters({ ...filters, when: e.target.value })} className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" data-testid="events-when">
            <option value="upcoming">Upcoming</option>
            <option value="past">Past</option>
            <option value="all">All</option>
          </select>
          <select value={filters.open_to_vendors} onChange={(e) => setFilters({ ...filters, open_to_vendors: e.target.value })} className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" data-testid="events-vendortype">
            <option value="">All events</option>
            <option value="true">Vendor opportunities only</option>
            <option value="false">Consumer events only</option>
          </select>
          <button
            onClick={useMyLocation}
            disabled={locating}
            className={`px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50 ${
              coords ? 'bg-fuchsia-500/20 text-fuchsia-200 border border-fuchsia-500/40' : 'bg-white/5 text-zinc-300 border border-white/10 hover:bg-white/10'
            }`}
            data-testid="events-locate"
          >
            {locating ? <Loader2 size={14} className="animate-spin" /> : <Locate size={14} />}
            {coords ? 'Near me' : 'Use my location'}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-fuchsia-400" /></div>
        ) : events.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center">
            <p className="text-white font-semibold">No events yet</p>
            <p className="text-zinc-400 text-xs mt-1">Be the first to post one — vending markets, festivals, conferences.</p>
            {user && (
              <button onClick={() => navigate('/events/new')} className="mt-4 px-4 py-2 rounded-full bg-yellow-400 text-black text-xs font-bold" data-testid="events-empty-create">
                Post the first event →
              </button>
            )}
          </div>
        ) : (
          <>
            {coords && nearby.length > 0 && (
              <section className="mb-10" data-testid="events-near-you">
                <h2 className="text-lg sm:text-xl font-bold font-syne mb-3 flex items-center gap-2">
                  <MapPin size={16} className="text-fuchsia-300" /> Near you
                  <span className="text-xs text-zinc-400 font-normal">{nearby.length} within 100km</span>
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {nearby.map((e) => <EventCard key={e.event_id} e={e} showDistance />)}
                </div>
              </section>
            )}

            <section data-testid="events-all">
              <h2 className="text-lg sm:text-xl font-bold font-syne mb-3 flex items-center gap-2">
                <Filter size={16} className="text-cyan-300" /> {coords ? 'All events' : 'Events'}
                <span className="text-xs text-zinc-400 font-normal">{allList.length} total</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {allList.map((e) => <EventCard key={e.event_id} e={e} showDistance={!!coords} />)}
              </div>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function EventCard({ e, showDistance }) {
  const loc = e.location || {};
  return (
    <Link
      to={`/events/${e.event_id}`}
      className="group rounded-2xl bg-[#13153A]/80 border border-white/5 hover:border-fuchsia-500/30 overflow-hidden transition-all hover:shadow-[0_12px_40px_-15px_rgba(217,70,239,0.5)]"
      data-testid={`event-card-${e.event_id}`}
    >
      <div className="aspect-[4/5] bg-black overflow-hidden relative">
        <img src={e.flyer_url} alt={e.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        {e.open_to_vendors && (
          <span className="absolute top-2 left-2 px-2 py-1 rounded-full bg-yellow-400 text-black text-[10px] font-bold uppercase tracking-wider">
            Vending
          </span>
        )}
        {e.is_featured && (
          <span className="absolute top-2 right-2 px-2 py-1 rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white text-[10px] font-bold uppercase tracking-wider">
            ★ Featured
          </span>
        )}
        {showDistance && e.distance_km !== null && e.distance_km !== undefined && (
          <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/70 text-white text-[10px] font-semibold">
            {e.distance_km}km
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-white font-bold text-base leading-tight line-clamp-2 mb-2 group-hover:text-fuchsia-300">{e.title}</h3>
        <div className="flex items-center gap-2 text-zinc-300 text-xs mb-1">
          <Calendar size={11} className="text-fuchsia-300" /> {fmtDate(e.date_iso)}
        </div>
        <div className="flex items-center gap-2 text-zinc-300 text-xs">
          <MapPin size={11} className="text-cyan-300" />
          <span className="truncate">{loc.city}{loc.state ? `, ${loc.state}` : ''}</span>
        </div>
        {e.tags && e.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {e.tags.slice(0, 3).map((t) => (
              <span key={t} className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-zinc-300 text-[10px] flex items-center gap-1">
                <Tag size={9} />{t}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}
